import React, { useCallback, useEffect, useState } from 'react'
import { DatePicker, Input, InputNumber, Space, Table, Tag, Typography, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs, { Dayjs } from 'dayjs'
import { get } from '../../lib/request'
import { AuditLogItem, PageData, errMsg, fmtTime } from '../../lib/api'

const { Text } = Typography

/** 操作日志（audit_logs 全局审计查询） */
const AuditLogTab: React.FC = () => {
  const [list, setList] = useState<AuditLogItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [adminUserId, setAdminUserId] = useState<number | undefined>(undefined)
  const [action, setAction] = useState('')
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)

  const fetchList = useCallback(() => {
    setLoading(true)
    get<PageData<AuditLogItem>>('/admin/audit-logs', {
      params: {
        page,
        pageSize: 20,
        ...(adminUserId ? { adminUserId } : {}),
        ...(action ? { action } : {}),
        ...(range?.[0] ? { from: range[0].startOf('day').toISOString() } : {}),
        ...(range?.[1] ? { to: range[1].endOf('day').toISOString() } : {}),
      },
    })
      .then((d) => {
        setList(d.items ?? [])
        setTotal(d.total ?? 0)
      })
      .catch((err) => message.warning(errMsg(err, '操作日志加载失败')))
      .finally(() => setLoading(false))
  }, [page, adminUserId, action, range])

  useEffect(() => { fetchList() }, [fetchList])

  const columns: ColumnsType<AuditLogItem> = [
    { title: 'ID', dataIndex: 'id', key: 'id', render: (v: number) => `#${v}`, width: 80 },
    {
      title: '操作人',
      dataIndex: 'adminUserId',
      key: 'adminUserId',
      render: (v?: number | null) => (v != null ? `#${v}` : '系统'),
    },
    {
      title: '动作',
      dataIndex: 'action',
      key: 'action',
      render: (v: string) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: '对象',
      key: 'target',
      render: (_: unknown, r) =>
        r.targetType ? `${r.targetType}${r.targetId != null ? ` #${r.targetId}` : ''}` : '—',
    },
    {
      title: '明细',
      dataIndex: 'detail',
      key: 'detail',
      ellipsis: true,
      render: (v?: string | null) => v ?? '—',
    },
    { title: 'IP', dataIndex: 'ip', key: 'ip', render: (v?: string | null) => v ?? '—' },
    { title: '时间', dataIndex: 'createdAt', key: 'createdAt', render: fmtTime },
  ]

  return (
    <>
      <Space style={{ marginBottom: 14 }} wrap>
        <InputNumber
          min={1}
          placeholder="操作人账号 ID"
          style={{ width: 150, minHeight: 44 }}
          value={adminUserId}
          onChange={(v) => { setAdminUserId(v ?? undefined); setPage(1) }}
        />
        <Input.Search
          allowClear
          placeholder="动作关键词（如 lead.deal）"
          style={{ width: 220 }}
          onSearch={(v) => { setAction(v); setPage(1) }}
        />
        <DatePicker.RangePicker
          value={range as never}
          onChange={(v) => { setRange(v); setPage(1) }}
          style={{ minHeight: 44 }}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          最近查询时间：{dayjs().format('HH:mm:ss')}
        </Text>
      </Space>
      <Table<AuditLogItem>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        scroll={{ x: 'max-content' }}
        style={{ background: '#fff', borderRadius: 12 }}
        pagination={{
          current: page, pageSize: 20, total,
          showTotal: (t) => `共 ${t} 条`, onChange: (p) => setPage(p),
        }}
      />
    </>
  )
}

export default AuditLogTab
